// DatabaseOperations.js - Database and SQL generation operations
import { getAuth } from 'firebase/auth';
import { getItineraryPublicUrl } from '../ItineraryCreator/utils/storageUtils';
import { getStoragePathComponents } from '../ItineraryCreator/utils/filenameUtils';
import { getPointType } from '../ItineraryCreator/utils/utils';

export class DatabaseOperations {
  constructor(dependencies) {
    this.itineraryState = dependencies.itineraryState;
    this.uiState = dependencies.uiState;
  }

  // Helper function to escape SQL strings
  escapeSql = (str) => {
    if (str === null || typeof str === 'undefined') return '';
    return String(str)
      .replace(/\\/g, '\\\\')
      .replace(/\u0000/g, '')
      .replace(/\r/g, '\\r')
      .replace(/\n/g, '\\n')
      .replace(/\t/g, '\\t')
      .replace(/'/g, "''");
  };

  // Helper function to sanitize a name for use in guide_path
  sanitizeName = (name) => {
    if (!name) return '';
    return name
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // strip diacritics
      .toLowerCase()
      .replace(/[\s'\-]+/g, '_')   // spaces, apostrophes, hyphens → _
      .replace(/[^a-z0-9_]/g, '')  // remove non-alphanumeric
      .replace(/_+/g, '_')         // collapse multiple _
      .replace(/^_|_$/g, '');      // strip leading/trailing _
  };

  formatSqlCoordinate = (value, fallbackValue = 0) => {
    const normalized = Number.parseFloat(value);
    if (Number.isFinite(normalized)) {
      return normalized;
    }

    const fallback = Number.parseFloat(fallbackValue);
    if (Number.isFinite(fallback)) {
      return fallback;
    }

    return 0;
  };

  hasValidCoordinate = (value) => {
    const normalized = Number.parseFloat(value);
    return Number.isFinite(normalized);
  };

  validateSqlPrerequisites = (itinerary) => {
    const errors = [];
    const invalidStops = [];

    if (!this.hasValidCoordinate(itinerary?.metadata?.coordinates?.latitude) ||
        !this.hasValidCoordinate(itinerary?.metadata?.coordinates?.longitude)) {
      errors.push('Guide coordinates are required before generating or executing SQL.');
    }

    (itinerary?.points || []).forEach((point, index) => {
      const hasLatitude = this.hasValidCoordinate(point?.coordinates?.latitude);
      const hasLongitude = this.hasValidCoordinate(point?.coordinates?.longitude);

      if (!hasLatitude || !hasLongitude) {
        invalidStops.push(point?.name?.trim() || `Stop ${index + 1}`);
      }
    });

    if (invalidStops.length > 0) {
      const stopList = invalidStops.slice(0, 5).join(', ');
      const suffix = invalidStops.length > 5 ? ` and ${invalidStops.length - 5} more` : '';
      errors.push(`Missing coordinates for: ${stopList}${suffix}. Add latitude and longitude to each stop before executing SQL.`);
    }

    if (errors.length > 0) {
      throw new Error(errors.join(' '));
    }
  };

  // Helper function to convert country name to ISO 2-letter code
  getCountryCode = (countryName) => {
    const countryMap = {
      'Portugal': 'PT',
      'Spain': 'ES',
      'France': 'FR',
      'Italy': 'IT',
      'Germany': 'DE',
      'United Kingdom': 'GB',
      'USA': 'US',
      'United States': 'US',
      'Greece': 'GR',
      'Netherlands': 'NL',
      'Belgium': 'BE',
      'Austria': 'AT',
      'Switzerland': 'CH',
      'Czech Republic': 'CZ',
      'Poland': 'PL',
      'Hungary': 'HU',
      'Croatia': 'HR',
      'Ireland': 'IE',
      'Denmark': 'DK',
      'Sweden': 'SE',
      'Norway': 'NO',
      'Finland': 'FI',
      'Turkey': 'TR',
      'Egypt': 'EG',
      'Morocco': 'MA',
      'Tunisia': 'TN',
      'Japan': 'JP',
      'China': 'CN',
      'India': 'IN',
      'Thailand': 'TH',
      'Vietnam': 'VN',
      'Australia': 'AU',
      'New Zealand': 'NZ',
      'Brazil': 'BR',
      'Argentina': 'AR',
      'Mexico': 'MX',
      'Canada': 'CA'
    };
    return countryMap[countryName] || countryName.substring(0, 2).toUpperCase();
  };

  // Generate SQL commands for itinerary
  generateSqlCommands = () => {
    const { itinerary, setSqlCommand, setErrors } = this.itineraryState;
    const { setShowSqlDialog } = this.uiState;

    try {
      this.validateSqlPrerequisites(itinerary);
      setErrors(prev => ({ ...prev, sql: null }));

      const itineraryId = itinerary.metadata.id || 'unknown';
      const name = this.escapeSql(itinerary.metadata.name || 'Untitled Guide');
      const description = this.escapeSql(itinerary.metadata.description || 'No description');
      const countryCode = this.getCountryCode(itinerary.metadata.country || '');
      const country = this.escapeSql(countryCode);
      const city = this.escapeSql(itinerary.metadata.city || '');
      const language = this.escapeSql(itinerary.metadata.language || 'en');
      const tier = this.escapeSql(itinerary.metadata.tier || 'free');
      const duration = this.escapeSql(itinerary.metadata.duration || '');
      const difficulty = this.escapeSql(itinerary.metadata.difficulty || 'moderate');
      const theme = this.escapeSql(itinerary.metadata.theme || '');
      const thumbnailUrl = this.escapeSql(itinerary.metadata.thumbnailUrl || '');
      const authorId = this.escapeSql(itinerary.metadata.authorId || '');
      const authorName = this.escapeSql(itinerary.metadata.authorName || '');
      const rating = itinerary.metadata.rating || 0;
      const reviewCount = itinerary.metadata.reviewCount || 0;
      const createdAt = itinerary.metadata.createdAt || new Date().toISOString();
      const updatedAt = itinerary.metadata.updatedAt || new Date().toISOString();
      const isIntroductoryStop = itinerary.metadata.isIntroductoryStop ? 1 : 0;
      const categories = this.escapeSql(JSON.stringify(itinerary.metadata.categories || []));
      // Add b2b_client tag if the checkbox is checked
      const tagsArray = [...(itinerary.metadata.tags || [])];
      if (this.itineraryState.b2bClient) {
        tagsArray.push('b2b_client');
      }
      const tags = this.escapeSql(JSON.stringify(tagsArray));
      const coordinates = itinerary.metadata.coordinates || {};
      const latitude = this.formatSqlCoordinate(coordinates.latitude);
      const longitude = this.formatSqlCoordinate(coordinates.longitude);

      // Helper function to format language code consistently with other parts of the app
      const formatLanguageCode = (code) => {
        if (!code) return 'en-us';
        const parts = code.split(/[-_]/);
        if (parts.length === 1) {
          const lang = parts[0].toLowerCase();
          return (lang === 'en') ? 'en-us' : `${lang}-${lang}`;
        } else {
          return `${parts[0].toLowerCase()}-${parts[1].toLowerCase()}`;
        }
      };

      // Generate the main itinerary SQL
      const guidePath = getStoragePathComponents(itinerary.metadata).fullPath;

      // Generate the public URL for the JSON file
      const websiteUrl = this.escapeSql(getItineraryPublicUrl(
        itinerary.metadata.country,
        itinerary.metadata.name,
        itinerary.metadata.filename,
        itinerary.metadata.language
      ));

      const sql = `
INSERT INTO \`itineraries_catalog\` (\`ID\`, \`Name\`, \`Description\`, \`Country\`, \`Locality\`, \`Language\`, \`Photo\`, \`Rating\`, \`user_ratings_total\`, \`Tags\`, \`Latitude\`, \`Longitude\`, \`Active\`, \`parent_guide_id\`, \`Website\`)
VALUES ('${itineraryId}', '${name}', '${description}', '${country}', '${city}', '${language}', '${thumbnailUrl}', ${rating}, ${reviewCount}, '${tags}', ${latitude}, ${longitude}, 1, NULL, '${websiteUrl}')
ON DUPLICATE KEY UPDATE
 \`Name\` = VALUES(\`Name\`),
 \`Description\` = VALUES(\`Description\`),
 \`Country\` = VALUES(\`Country\`),
 \`Locality\` = VALUES(\`Locality\`),
 \`Language\` = VALUES(\`Language\`),
 \`Photo\` = VALUES(\`Photo\`),
 \`Rating\` = VALUES(\`Rating\`),
 \`user_ratings_total\` = VALUES(\`user_ratings_total\`),
 \`Tags\` = VALUES(\`Tags\`),
 \`Latitude\` = VALUES(\`Latitude\`),
 \`Longitude\` = VALUES(\`Longitude\`),
 \`Website\` = VALUES(\`Website\`);
`;

      // Generate SQL for stops
      console.log('[SQL DEBUG] Generating SQL for stops. Points:', itinerary.points.map(p => ({ name: p.name, tier: p.tier, id: p.id })));
      let stopsSql = '';
      if (itinerary.points && itinerary.points.length > 0) {
        itinerary.points.forEach((point, index) => {
          if (getPointType(point) === 'popup_stop') {
            return;
          }
          const stopId = this.escapeSql(point.id || `${itineraryId}_${index + 1}`);
          const stopName = this.escapeSql(point.name || `Stop ${index + 1}`);
          const stopDescription = this.escapeSql(point.description || '');
          const stopCategory = this.escapeSql(point.category || 'Other');
          const stopTier = this.escapeSql(point.tier || 'free');
          console.log(`[SQL DEBUG] Stop ${index + 1}: "${point.name}" -> tier="${point.tier}" -> stopTier="${stopTier}"`);
          const stopCoordinates = point.coordinates || {};
          const stopLatitude = this.formatSqlCoordinate(stopCoordinates.latitude, coordinates.latitude);
          const stopLongitude = this.formatSqlCoordinate(stopCoordinates.longitude, coordinates.longitude);
          const stopImageUrl = this.escapeSql(point.imageUrl || '');
          const stopAudioUrl = this.escapeSql(point.audioUrl || '');
          const stopLanguage = this.escapeSql(point.language || language);
          const stopOrder = index + 1;

          const stopSql = `
INSERT INTO \`itinerary_stops\` (\`id\`, \`Itinerary_ID\`, \`Name\`, \`Description\`, \`Category\`, \`Tier\`, \`Latitude\`, \`Longitude\`, \`Photo_Url\`, \`Audio_Url\`, \`Language\`)
VALUES ('${stopId}', '${itineraryId}', '${stopName}', '${stopDescription}', '${stopCategory}', '${stopTier}', ${stopLatitude}, ${stopLongitude}, '${stopImageUrl}', '${stopAudioUrl}', '${stopLanguage}')
ON DUPLICATE KEY UPDATE
 \`Name\` = VALUES(\`Name\`),
 \`Description\` = VALUES(\`Description\`),
 \`Category\` = VALUES(\`Category\`),
 \`Tier\` = VALUES(\`Tier\`),
 \`Latitude\` = VALUES(\`Latitude\`),
 \`Longitude\` = VALUES(\`Longitude\`),
 \`Photo_Url\` = VALUES(\`Photo_Url\`),
 \`Audio_Url\` = VALUES(\`Audio_Url\`),
 \`Language\` = VALUES(\`Language\`);
`;
          stopsSql += stopSql;
        });
      }

      // Generate SQL for QR codes
      let qrSqlCommands = '';
      const guideQrCode = this.escapeSql(itinerary.metadata.parent_guide_id || itineraryId);
      const guideQrUrl = `https://guidesofia.com/qr/${guideQrCode}`;
      const guideTier = this.escapeSql(itinerary.metadata.tier || 'free');
      const guideName = this.escapeSql(itinerary.metadata.name);
      const guideDescription = this.escapeSql(itinerary.metadata.description || 'Audio guide.');
      const guideLat = this.formatSqlCoordinate(itinerary.metadata.coordinates?.latitude);
      const guideLng = this.formatSqlCoordinate(itinerary.metadata.coordinates?.longitude);

      if (guideQrCode) {
        qrSqlCommands += `
INSERT INTO \`qr_codes\` (\`qr_code\`, \`qr_type\`, \`full_qr_code\`, \`guide_path\`, \`stop_id\`, \`tier\`, \`created_by\`, \`is_active\`, \`guide_name\`, \`stop_name\`, \`description\`, \`location_lat\`, \`location_lng\`, \`Itinerary_ID\`)
VALUES ('${guideQrCode}', 'guide', '${guideQrUrl}', '${this.escapeSql(guidePath)}', NULL, '${guideTier}', 'admin', 1, '${guideName}', NULL, '${guideDescription}', ${guideLat}, ${guideLng}, '${itineraryId}')
ON DUPLICATE KEY UPDATE
 \`qr_type\` = VALUES(\`qr_type\`),
 \`full_qr_code\` = VALUES(\`full_qr_code\`),
 \`guide_path\` = VALUES(\`guide_path\`),
 \`stop_id\` = VALUES(\`stop_id\`),
 \`tier\` = VALUES(\`tier\`),
 \`guide_name\` = VALUES(\`guide_name\`),
 \`stop_name\` = VALUES(\`stop_name\`),
 \`description\` = VALUES(\`description\`),
 \`location_lat\` = VALUES(\`location_lat\`),
 \`location_lng\` = VALUES(\`location_lng\`),
 \`Itinerary_ID\` = VALUES(\`Itinerary_ID\`);
`;
      }

      if (itinerary.points && itinerary.points.length > 0) {
        itinerary.points.forEach(point => {
          const stop_id = point.id;
          const qr_code = stop_id;
          const full_qr_code = `https://guidesofia.com/qr/${qr_code}`;
          const qr_type = getPointType(point) === 'popup_stop' ? 'popup_stop' : 'stop';
          const tier = this.escapeSql(point.tier || itinerary.metadata.tier || 'free');
          const guide_name = this.escapeSql(itinerary.metadata.name);
          const stop_name = this.escapeSql(point.name);
          const description = this.escapeSql(point.description || 'Audio guide stop.');
          const location_lat = this.formatSqlCoordinate(point.coordinates?.latitude, itinerary.metadata.coordinates?.latitude);
          const location_lng = this.formatSqlCoordinate(point.coordinates?.longitude, itinerary.metadata.coordinates?.longitude);

          const qrSql = `
INSERT INTO \`qr_codes\` (\`qr_code\`, \`qr_type\`, \`full_qr_code\`, \`guide_path\`, \`stop_id\`, \`tier\`, \`created_by\`, \`is_active\`, \`guide_name\`, \`stop_name\`, \`description\`, \`location_lat\`, \`location_lng\`, \`Itinerary_ID\`)
VALUES ('${qr_code}', '${qr_type}', '${full_qr_code}', '${this.escapeSql(guidePath)}', '${stop_id}', '${tier}', 'admin', 1, '${guide_name}', '${stop_name}', '${description}', ${location_lat}, ${location_lng}, '${itineraryId}')
ON DUPLICATE KEY UPDATE
 \`qr_type\` = VALUES(\`qr_type\`),
 \`full_qr_code\` = VALUES(\`full_qr_code\`),
 \`guide_path\` = VALUES(\`guide_path\`),
 \`stop_id\` = VALUES(\`stop_id\`),
 \`tier\` = VALUES(\`tier\`),
 \`guide_name\` = VALUES(\`guide_name\`),
 \`stop_name\` = VALUES(\`stop_name\`),
 \`description\` = VALUES(\`description\`),
 \`location_lat\` = VALUES(\`location_lat\`),
 \`location_lng\` = VALUES(\`location_lng\`),
 \`Itinerary_ID\` = VALUES(\`Itinerary_ID\`);
`;
          qrSqlCommands += qrSql;
        });
      }

      const fullSql = sql + '\n\n' + stopsSql + '\n\n' + qrSqlCommands;

      setSqlCommand(fullSql);
      setShowSqlDialog(true);
    } catch (error) {
      setErrors(prev => ({ ...prev, sql: `Error generating SQL: ${error.message}` }));
    }
  };

  // Copy SQL to clipboard
  copySqlToClipboard = async () => {
    const { sqlCommand } = this.itineraryState;
    const { setCopied } = this.uiState;

    try {
      await navigator.clipboard.writeText(sqlCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy SQL:', error);
    }
  };

  // Execute SQL commands on the database
  executeSql = async () => {
    const { itinerary, sqlCommand, setSqlCommand, setErrors } = this.itineraryState;
    const { setExecuting, setExecutionResult } = this.uiState;

    setExecuting(true);
    setExecutionResult(null);
    setErrors(prev => ({ ...prev, sql: null }));

    // Always regenerate SQL from current state to reflect any changes
    let commandToExecute = null;
    if (!commandToExecute) {
      try {
        this.validateSqlPrerequisites(itinerary);

        // Generate SQL inline (same logic as generateSqlCommands but without showing dialog)
        const itineraryId = itinerary.metadata.id || 'unknown';
        const name = this.escapeSql(itinerary.metadata.name || 'Untitled Guide');
        const description = this.escapeSql(itinerary.metadata.description || 'No description');
        const countryCode = this.getCountryCode(itinerary.metadata.country || '');
        const country = this.escapeSql(countryCode);
        const city = this.escapeSql(itinerary.metadata.city || '');
        const language = this.escapeSql(itinerary.metadata.language || 'en');
        const tier = this.escapeSql(itinerary.metadata.tier || 'free');
        const duration = this.escapeSql(itinerary.metadata.duration || '');
        const difficulty = this.escapeSql(itinerary.metadata.difficulty || 'moderate');
        const theme = this.escapeSql(itinerary.metadata.theme || '');
        const thumbnailUrl = this.escapeSql(itinerary.metadata.thumbnailUrl || '');
        const authorId = this.escapeSql(itinerary.metadata.authorId || '');
        const authorName = this.escapeSql(itinerary.metadata.authorName || '');
        const rating = itinerary.metadata.rating || 0;
        const reviewCount = itinerary.metadata.reviewCount || 0;
        const createdAt = itinerary.metadata.createdAt || new Date().toISOString();
        const updatedAt = itinerary.metadata.updatedAt || new Date().toISOString();
        const isIntroductoryStop = itinerary.metadata.isIntroductoryStop ? 1 : 0;
        const categories = this.escapeSql(JSON.stringify(itinerary.metadata.categories || []));
        // Add b2b_client tag if the checkbox is checked
        const tagsArray = [...(itinerary.metadata.tags || [])];
        if (this.itineraryState.b2bClient) {
          tagsArray.push('b2b_client');
        }
        const tags = this.escapeSql(JSON.stringify(tagsArray));
        const coordinates = itinerary.metadata.coordinates || {};
        const latitude = this.formatSqlCoordinate(coordinates.latitude);
        const longitude = this.formatSqlCoordinate(coordinates.longitude);

        const formatLanguageCode = (code) => {
          if (!code) return 'en-us';
          const parts = code.split(/[-_]/);
          if (parts.length === 1) {
            const lang = parts[0].toLowerCase();
            return (lang === 'en') ? 'en-us' : `${lang}-${lang}`;
          } else {
            return `${parts[0].toLowerCase()}-${parts[1].toLowerCase()}`;
          }
        };

        const guidePath = getStoragePathComponents(itinerary.metadata).fullPath;

        // Generate the public URL for the JSON file
        const websiteUrl = this.escapeSql(getItineraryPublicUrl(
          itinerary.metadata.country,
          itinerary.metadata.name,
          itinerary.metadata.filename,
          itinerary.metadata.language
        ));

        const parentGuideId = this.escapeSql(itinerary.metadata.parent_guide_id || itineraryId);

        const sql = `
INSERT INTO \`itineraries_catalog\` (\`ID\`, \`Name\`, \`Description\`, \`Country\`, \`Locality\`, \`Language\`, \`Photo\`, \`Rating\`, \`user_ratings_total\`, \`Tags\`, \`Latitude\`, \`Longitude\`, \`Active\`, \`parent_guide_id\`, \`Website\`)
VALUES ('${itineraryId}', '${name}', '${description}', '${country}', '${city}', '${language}', '${thumbnailUrl}', ${rating}, ${reviewCount}, '${tags}', ${latitude}, ${longitude}, 1, '${parentGuideId}', '${websiteUrl}')
ON DUPLICATE KEY UPDATE
 \`Name\` = VALUES(\`Name\`),
 \`Description\` = VALUES(\`Description\`),
 \`Country\` = VALUES(\`Country\`),
 \`Locality\` = VALUES(\`Locality\`),
 \`Language\` = VALUES(\`Language\`),
 \`Photo\` = VALUES(\`Photo\`),
 \`Rating\` = VALUES(\`Rating\`),
 \`user_ratings_total\` = VALUES(\`user_ratings_total\`),
 \`Tags\` = VALUES(\`Tags\`),
 \`Latitude\` = VALUES(\`Latitude\`),
 \`Longitude\` = VALUES(\`Longitude\`),
 \`parent_guide_id\` = VALUES(\`parent_guide_id\`),
 \`Website\` = VALUES(\`Website\`);
`;

        console.log('[SQL DEBUG executeSql] Points:', itinerary.points.map(p => ({ name: p.name, tier: p.tier, id: p.id })));
        let stopsSql = '';
        if (itinerary.points && itinerary.points.length > 0) {
          itinerary.points.forEach((point, index) => {
          if (getPointType(point) === 'popup_stop') {
            return;
          }
            const stopId = this.escapeSql(point.id || `${itineraryId}_${index + 1}`);
            const stopName = this.escapeSql(point.name || `Stop ${index + 1}`);
            const stopDescription = this.escapeSql(point.description || '');
            const stopCategory = this.escapeSql(point.category || 'Other');
            const stopTier = this.escapeSql(point.tier || 'free');
            const stopCoordinates = point.coordinates || {};
            const stopLatitude = this.formatSqlCoordinate(stopCoordinates.latitude, coordinates.latitude);
            const stopLongitude = this.formatSqlCoordinate(stopCoordinates.longitude, coordinates.longitude);
            const stopImageUrl = this.escapeSql(point.imageUrl || '');
            const stopAudioUrl = this.escapeSql(point.audioUrl || '');
            const stopLanguage = this.escapeSql(point.language || language);
            const stopOrder = index + 1;

            const stopSql = `
INSERT INTO \`itinerary_stops\` (\`id\`, \`Itinerary_ID\`, \`Name\`, \`Description\`, \`Category\`, \`Tier\`, \`Latitude\`, \`Longitude\`, \`Photo_Url\`, \`Audio_Url\`, \`Language\`)
VALUES ('${stopId}', '${itineraryId}', '${stopName}', '${stopDescription}', '${stopCategory}', '${stopTier}', ${stopLatitude}, ${stopLongitude}, '${stopImageUrl}', '${stopAudioUrl}', '${stopLanguage}')
ON DUPLICATE KEY UPDATE
 \`Name\` = VALUES(\`Name\`),
 \`Description\` = VALUES(\`Description\`),
 \`Category\` = VALUES(\`Category\`),
 \`Tier\` = VALUES(\`Tier\`),
 \`Latitude\` = VALUES(\`Latitude\`),
 \`Longitude\` = VALUES(\`Longitude\`),
 \`Photo_Url\` = VALUES(\`Photo_Url\`),
 \`Audio_Url\` = VALUES(\`Audio_Url\`),
 \`Language\` = VALUES(\`Language\`);
`;
            stopsSql += stopSql;
          });
        }

        let qrSqlCommands = '';
        const guideQrCode = parentGuideId;
        const guideQrUrl = `https://guidesofia.com/qr/${guideQrCode}`;
        const guideTier = this.escapeSql(itinerary.metadata.tier || 'free');
        const guideName = this.escapeSql(itinerary.metadata.name);
        const guideDescription = this.escapeSql(itinerary.metadata.description || 'Audio guide.');
        const guideLat = this.formatSqlCoordinate(itinerary.metadata.coordinates?.latitude);
        const guideLng = this.formatSqlCoordinate(itinerary.metadata.coordinates?.longitude);

        if (guideQrCode) {
          qrSqlCommands += `
INSERT INTO \`qr_codes\` (\`qr_code\`, \`qr_type\`, \`full_qr_code\`, \`guide_path\`, \`stop_id\`, \`tier\`, \`created_by\`, \`is_active\`, \`guide_name\`, \`stop_name\`, \`description\`, \`location_lat\`, \`location_lng\`, \`Itinerary_ID\`)
VALUES ('${guideQrCode}', 'guide', '${guideQrUrl}', '${this.escapeSql(guidePath)}', NULL, '${guideTier}', 'admin', 1, '${guideName}', NULL, '${guideDescription}', ${guideLat}, ${guideLng}, '${itineraryId}')
ON DUPLICATE KEY UPDATE
 \`qr_type\` = VALUES(\`qr_type\`),
 \`full_qr_code\` = VALUES(\`full_qr_code\`),
 \`guide_path\` = VALUES(\`guide_path\`),
 \`stop_id\` = VALUES(\`stop_id\`),
 \`tier\` = VALUES(\`tier\`),
 \`guide_name\` = VALUES(\`guide_name\`),
 \`stop_name\` = VALUES(\`stop_name\`),
 \`description\` = VALUES(\`description\`),
 \`location_lat\` = VALUES(\`location_lat\`),
 \`location_lng\` = VALUES(\`location_lng\`),
 \`Itinerary_ID\` = VALUES(\`Itinerary_ID\`);
`;
        }

        if (itinerary.points && itinerary.points.length > 0) {
          itinerary.points.forEach(point => {
            const stop_id = point.id;
            const qr_code = stop_id;
            const full_qr_code = `https://guidesofia.com/qr/${qr_code}`;
            const qr_type = getPointType(point) === 'popup_stop' ? 'popup_stop' : 'stop';
            const tier = this.escapeSql(point.tier || 'free');
            const guide_name = this.escapeSql(itinerary.metadata.name);
            const stop_name = this.escapeSql(point.name);
            const description = this.escapeSql(point.description || 'Audio guide stop.');
            const location_lat = this.formatSqlCoordinate(point.coordinates?.latitude, itinerary.metadata.coordinates?.latitude);
            const location_lng = this.formatSqlCoordinate(point.coordinates?.longitude, itinerary.metadata.coordinates?.longitude);

            const qrSql = `
INSERT INTO \`qr_codes\` (\`qr_code\`, \`qr_type\`, \`full_qr_code\`, \`guide_path\`, \`stop_id\`, \`tier\`, \`created_by\`, \`is_active\`, \`guide_name\`, \`stop_name\`, \`description\`, \`location_lat\`, \`location_lng\`, \`Itinerary_ID\`)
VALUES ('${qr_code}', '${qr_type}', '${full_qr_code}', '${this.escapeSql(guidePath)}', '${stop_id}', '${tier}', 'admin', 1, '${guide_name}', '${stop_name}', '${description}', ${location_lat}, ${location_lng}, '${itineraryId}')
ON DUPLICATE KEY UPDATE
 \`qr_type\` = VALUES(\`qr_type\`),
 \`full_qr_code\` = VALUES(\`full_qr_code\`),
 \`guide_path\` = VALUES(\`guide_path\`),
 \`stop_id\` = VALUES(\`stop_id\`),
 \`tier\` = VALUES(\`tier\`),
 \`guide_name\` = VALUES(\`guide_name\`),
 \`stop_name\` = VALUES(\`stop_name\`),
 \`description\` = VALUES(\`description\`),
 \`location_lat\` = VALUES(\`location_lat\`),
 \`location_lng\` = VALUES(\`location_lng\`),
 \`Itinerary_ID\` = VALUES(\`Itinerary_ID\`);
`;
            qrSqlCommands += qrSql;
          });
        }

        commandToExecute = sql + '\n\n' + stopsSql + '\n\n' + qrSqlCommands;
        setSqlCommand(commandToExecute);
      } catch (error) {
        setExecuting(false);
        setErrors(prev => ({ ...prev, sql: error.message }));
        setExecutionResult({
          success: false,
          message: error.message
        });
        return;
      }
    }

    try {
      // Get the Firebase auth token
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();

      // Call the Firebase Function
      const response = await fetch('https://us-central1-guidesofia-access-control.cloudfunctions.net/executeSQL', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sqlCommands: commandToExecute
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to execute SQL');
      }

      setExecutionResult({
        success: true,
        message: result.message || 'SQL executed successfully',
        affectedRows: result.affectedRows
      });

    } catch (error) {
      console.error('Failed to execute SQL:', error);
      setExecutionResult({
        success: false,
        message: error.message || 'Failed to execute SQL commands'
      });
    } finally {
      setExecuting(false);
    }
  };
}

